// review-4.twig

import React from 'react'

import {Content, Headline, workshops, LazyImageAdapter} from '@miro-site/components_utils'
import type {Assets} from '@miro-site/components_utils'
import {Box, BoxProps, Heading, Section} from '@miro-site/core'
import styled, {css, useTheme} from 'styled-components'

import {useLocalization} from '@miro-site/localization'

const {WORKSHOPS_BG_COLORS} = workshops

type BackgroundType = keyof typeof WORKSHOPS_BG_COLORS

interface TestimonialProps {
  rtl?: 'no' | 'true'
  background?: BackgroundType
  image?: Assets
  headline?: React.ComponentProps<typeof Headline>
  name?: React.ComponentProps<typeof Content>
  post?: React.ComponentProps<typeof Content>
}

interface TestimonialBgProps extends BoxProps {
  $background: BackgroundType
}

const TestimonialBg = styled(Box).attrs({})<TestimonialBgProps>`
  ${({$background, theme}) =>
    $background &&
    css`
      background: ${theme.colors[WORKSHOPS_BG_COLORS[$background]]};
    `}
`

const Row = styled(Box).attrs({$my: [null, 0], $mx: [null, -20]})`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  position: relative;
  z-index: 3;
`

const Col = styled(Box).attrs({
  $py: [0],
  $px: [20],
  $styles: {
    flex: [null, '0 0 66.66%'],
  },
})``

const ImageWrapper = styled(Box).attrs({
  $styles: {
    transform: ['translateX(0)', 'translateX(-50%)'],
    top: [-40, -46],
    bottom: [null, -46],
    right: [0, null],
    left: [0, '50%'],
    height: [360, null],
    width: [null, 1120],
  },
})`
  position: absolute;
`

const ImageContainer = styled(Box).attrs({
  $styles: {
    transform: ['translateX(0)', 'translateY(-50%)'],
    top: [0, '50%'],
    left: [0, '58.31%'],
    position: ['relative', undefined],
    height: '100%',
    maxWidth: ['100%', null],
    width: ['100%', 500],
  },
})`
  position: absolute;
`
interface TestimonialContainerProps extends BoxProps {
  $isRtl?: boolean
}

const TestimonialContainer = styled(Box).attrs({
  $pt: [336, 240],
  $pb: [96, 240],
  $px: 0,
  $styles: {
    maxWidth: [null, 1120],
    minHeight: [null, 800],
  },
})<TestimonialContainerProps>`
  margin: auto;
  position: relative;

  ${Heading} {
    position: relative;

    ${({theme}) =>
      theme.normalizeStyles({
        maxWidth: [null, 645],
      })}

    &::after {
      content: '”';
    }

    &::before {
      content: '“';
      position: absolute;
      top: 0;

      ${({theme}) =>
        theme.normalizeStyles({
          left: [-17, -22],
        })}
    }

    &[lang='ja'] {
      &:before {
        content: '「';

        ${({theme}) =>
          theme.normalizeStyles({
            left: [-27, -32],
          })}
      }

      &:after {
        content: '。」';
      }
    }
  }

  ${({$isRtl = false, theme}) =>
    $isRtl &&
    css`
      ${Row} {
        ${theme.normalizeStyles({
          left: [null, '33.33%'],
          paddingLeft: [null, 10],
        })}
      }

      ${ImageContainer} {
        ${theme.normalizeStyles({
          left: [0, '-14%'],
        })}
      }
    `}
`

const Testimonial: React.FC<TestimonialProps> = ({
  background = 'workshops-color-pink',
  image,
  headline,
  name,
  post,
  rtl,
}) => {
  const theme = useTheme()

  const {currentLocale} = useLocalization()

  return (
    <Section data-place="Testimonial Section" type="large" $styles={{overflow: 'hidden'}}>
      <TestimonialBg $background={background}>
        <TestimonialContainer $isRtl={rtl === 'true'}>
          {image && (
            <ImageWrapper>
              <ImageContainer>
                <LazyImageAdapter assets={image}></LazyImageAdapter>
              </ImageContainer>
            </ImageWrapper>
          )}

          <Row>
            <Col>
              {headline && (
                <Headline
                  {...headline}
                  size={headline.size || 'small'}
                  tag={headline.tag || 'h3'}
                  color={workshops.isHighContrastColor(background) ? 'white' : undefined}
                  headingProps={{lang: currentLocale, $styles: {maxWidth: [null, 645]}}}
                ></Headline>
              )}

              <Box $mt={[theme.spaces.xl, theme.spaces.xxl]} $styles={{maxWidth: [null, 550]}}>
                {name && (
                  <Content
                    {...name}
                    color={workshops.isHighContrastColor(background) ? 'white' : 'dark'}
                    size="large"
                    weight="bold"
                    textProps={{
                      $styles: {
                        marginTop: [theme.spaces.xl, theme.spaces.xxl],
                        fontFamily: '"Spoof", sans-serif',
                        fontFeatureSettings: '"zero", "ss02", "ss05"',
                      },
                    }}
                  ></Content>
                )}

                {post && (
                  <Content
                    {...post}
                    color={workshops.isHighContrastColor(background) ? 'white' : 'dark'}
                  ></Content>
                )}
              </Box>
            </Col>
          </Row>
        </TestimonialContainer>
      </TestimonialBg>
    </Section>
  )
}

export default Testimonial
